import {
	ArchiveBoxIcon,
	ArrowTopRightOnSquareIcon,
	ArrowUpRightIcon,
	BuildingOfficeIcon,
	PlusIcon,
	TruckIcon,
} from '@heroicons/react/24/outline';
import { GQLOrderItemTileFragment, GQLOrderTrackingFragment, GQLOrderTrackingTypeType } from '@theprintbar/graphql';
import { OrderShipmentGroup, getOrderShipmentGroups, getTrackingUrl } from '@theprintbar/shared';
import { ButtonSize, OutlineAnchorButton, OutlineLinkButton, Pill, PrimaryAnchorButton } from '../../../components';
import { LineItemTable } from '../../../tables';

interface OrderLineItemsProps {
	items?: GQLOrderItemTileFragment[] | null;
	tracking?: GQLOrderTrackingFragment[] | null;
	//The order's own shipment, which is all an order pushed before we tracked it per line item has
	orderShipmentId?: string | null;
	staff?: boolean;
	//Items can only be added while the order hasn't been approved or pushed to the warehouse
	canAddItems?: boolean;
	generateLink?: (id: string) => string;
	webDomain?: string;
	warehouseURL?: string;
}

//A warehouse group has no shipment until FE has placed the quantity in one, so there's nothing to link to
function ShipmentLink({ shipmentId, warehouseURL }: { shipmentId: string; warehouseURL?: string }) {
	if (!shipmentId) return <span className="text-sm text-grey">Not yet assigned</span>;

	return (
		<a href={`${warehouseURL}/shipments/${shipmentId}`} className="underline flex items-center text-sm" target="_blank" rel="noreferrer">
			{shipmentId}
			<ArrowTopRightOnSquareIcon className="w-4 h-4 ml-1" />
		</a>
	);
}

export function OrderLineItems({
	items,
	tracking,
	orderShipmentId,
	staff,
	canAddItems,
	generateLink,
	webDomain,
	warehouseURL,
}: OrderLineItemsProps) {
	const shipmentGroups = getOrderShipmentGroups(items, tracking);

	//Nothing is split while there's a single warehouse group, so a group with no shipment of its own
	//belongs to whatever the order was pushed as - old orders never had the id written to their items
	const singleWarehouseGroup = shipmentGroups.filter(group => group.type === GQLOrderTrackingTypeType.Warehouse).length === 1;
	const groupShipmentId = (group: OrderShipmentGroup) => group.shipmentId || (singleWarehouseGroup ? orderShipmentId || '' : '');

	const addItemButton = !!staff && !!canAddItems && (
		<OutlineLinkButton size={ButtonSize.S} icon={PlusIcon} to={'./add'} className="text-sm">
			Add Item
		</OutlineLinkButton>
	);

	//Everything is going out together, so there's nothing to split up
	if (shipmentGroups.length <= 1) {
		return (
			<>
				<div className="flex justify-between items-center">
					<div className="flex items-center gap-2">
						<h2 className="flex items-center gap-2">
							Line items
							{staff && ' - '}
							<div className="text-base">
								{!staff ? (
									''
								) : shipmentGroups[0].type === GQLOrderTrackingTypeType.Scr ? (
									'SPS'
								) : shipmentGroups[0].type === GQLOrderTrackingTypeType.Trends ? (
									'Trends'
								) : (
									<ShipmentLink shipmentId={groupShipmentId(shipmentGroups[0])} warehouseURL={warehouseURL} />
								)}
							</div>
						</h2>
					</div>

					{addItemButton}
				</div>
				<LineItemTable staff={staff} generateLink={generateLink} items={items} webDomain={webDomain} />
			</>
		);
	}

	return (
		<div className="space-y-6">
			{shipmentGroups.map((group, index) => (
				<div key={group.key || 'unassigned'} className="space-y-2">
					<div className="flex items-center justify-between gap-4">
						<div className="flex items-center gap-2">
							<h2 className="flex items-center gap-2">
								Shipment #{index + 1}
								{staff && ' - '}
								<div className="text-base">
									{!staff ? (
										''
									) : group.type === GQLOrderTrackingTypeType.Scr ? (
										'SPS'
									) : group.type === GQLOrderTrackingTypeType.Trends ? (
										'Trends'
									) : (
										<ShipmentLink shipmentId={groupShipmentId(group)} warehouseURL={warehouseURL} />
									)}
								</div>
							</h2>
						</div>
						<div className="flex items-center gap-2">
							{group.tracking ? (
								<a href={getTrackingUrl(group.tracking.trackingNumber)} target="_blank" rel="noreferrer">
									<Pill className="bg-blue text-white py-0.5 px-2 underline">
										<TruckIcon className="w-4 h-4 mr-1" />
										Tracking #{group.tracking.trackingNumber}
									</Pill>
								</a>
							) : (
								!!staff &&
								group.requiresTracking && (
									<OutlineLinkButton
										size={ButtonSize.S}
										to={`./tracking?shipment=${encodeURIComponent(group.key)}`}
										className="text-sm"
									>
										Mark as Shipped
									</OutlineLinkButton>
								)
							)}

							{addItemButton}
						</div>
					</div>
					<LineItemTable staff={staff} generateLink={generateLink} items={group.items} webDomain={webDomain} />
				</div>
			))}
		</div>
	);
}
